import * as React from 'react';
import { Theme, useTheme } from '@mui/material/styles';
import { Cabinet } from '../../models/cabinet';
import PucEditor from '../puc-editor';
import { getCabinetConfiguration, upsertCabinet, putCabinetDevice } from '../../utilities/api-utils';
import store, { useAppSelector } from '../../store';
import allCountries from '../../utilities/all-countries';

import { 
  Box,
  Button,
  DialogContent, 
  DialogTitle,
  Switch,
  Tabs,
  Tab,
  TextField,
  Typography,
  Select,
  SelectChangeEvent,
  OutlinedInput,
  MenuItem
} from '@mui/material';
import { useState, useEffect } from 'react';
import { Puc } from '../../models/puc';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
};

function CabinetTabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`cabinet-tabpanel-${index}`}
      aria-labelledby={`cabinet-tab-${index}`}
      {...other}>
      {value === index && (
        <Box sx={{ marginTop: '20px', marginLeft: '20px' }}>
          {children}
        </Box>
      )}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `cabinet-tab-${index}`,
    'aria-controls': `cabinet-tabpanel-${index}`,
  };
}

interface CabinetDialogProps {
  editingCabinet: string,
  selectedStoreId: string,
  setDialog(arg: boolean): void,
  refresh(): void
}

function getStyles(country: string, smsCountries: string[], theme: Theme) {
  return {
    fontWeight:
      smsCountries.indexOf(country) === -1
        ? theme.typography.fontWeightRegular
        : theme.typography.fontWeightMedium,
  };
}

function CabinetDialog({ editingCabinet, selectedStoreId, setDialog, refresh }: CabinetDialogProps) {
  const [value, setValue] = React.useState(0);

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
  };
  
  const theme = useTheme();

  const { stores } = useAppSelector((store) => store.store);
  const [storeIdErrorText, setStoreIdErrorText] = useState<string>('');
  const [descriptionErrorText, setDescriptionErrorText] = useState<string>('');
  const [businessNameErrorText, setBusinessNameErrorText] = useState<string>('');
  const [businessAddressErrorText, setBusinessAddressErrorText] = useState<string>('');
  const [businessPhoneErrorText, setBusinessPhoneErrorText] = useState<string>('');
  const [cabinetLocationErrorText, setCabinetLocationErrorText] = useState<string>('');
  const [brandErrorText, setBrandErrorText] = useState<string>('');
  const [orderManagerCodeErrorText, setOrderManagerCodeErrorText] = useState<string>('');
  const [hasStoreIdError, setHasStoreIdError] = useState<boolean>(false);
  const [hasDescriptionError, setHasDescriptionError] = useState<boolean>(false);
  const [hasBusinessNameError, setHasBusinessNameError] = useState<boolean>(false);
  const [hasBusinessAddressError, setHasBusinessAddressError] = useState<boolean>(false);
  const [hasBusinessPhoneError, setHasBusinessPhoneError] = useState<boolean>(false);
  const [hasCabinetLocationError, setHasCabinetLocationError] = useState<boolean>(false);
  const [hasBrandError, setHasBrandError] = useState<boolean>(false);
  const [hasOrderManagerCodeError, setHasOrderManagerCodeError] = useState<boolean>(false);
  const [intermediateStoreId, setIntermediateStoreId] = useState<string>('');
  const [cabinetObject, setCabinetObject] = useState<Cabinet>();
  const [storeId, setStoreId] = useState<string>('');
  const [cabinetId, setCabinetId] = useState<string>('');
  const [description, setDescription] = useState<string>('');
  const [businessName, setBusinessName] = useState<string>('');
  const [businessAddress, setBusinessAddress] = useState<string>('');
  const [businessPhone, setBusinessPhone] = useState<string>('');
  const [cabinetLocation, setCabinetLocation] = useState<string>('');
  const [hardwareConfig, setHardwareConfig] = useState<Array<Puc>>([]);
  const [smsCountries, setSmsCountries] = useState<Array<string>>([]);
  const [enableSms, setEnableSms] = useState<boolean>(true);
  const [enableOrderPrint, setEnableOrderPrint] = useState<boolean>(false);
  const [isNewCabinet, setIsNewCabinet] = useState<boolean>(false);
  const [deviceSerial, setDeviceSerial] = useState<string>('CHANGE ME');
  const [movingCabinet, setMovingCabinet] = useState<boolean>(false);
  const [cabinetBrand, setCabinetBrand] = useState<string>('');
  const [orderManagerCode, setOrderManagerCode] = useState<string>('008');
  const [orderManagerQrCode, setOrderManagerQrCode] = useState<string>('');
  const [isFormValid, setIsFormValid] = useState<boolean>(false);
  const [hasLighting, setHasLighting] = useState<boolean>(false);
  const [lightingColorOpen, setLightingColorOpen] = useState<string>('#ffffff');
  const [hasLightingColorOpenError, setHasLightingColorOpenError] = useState<boolean>(false);
  const [lightingColorOpenErrorText, setLightingColorOpenErrorText] = useState<string>('');
  const [lightingColorClosed, setLightingColorClosed] = useState<string>('#ffffff');
  const [hasLightingColorClosedError, setHasLightingColorClosedError] = useState<boolean>(false);
  const [lightingColorClosedErrorText, setLightingColorClosedErrorText] = useState<string>('');
  const [temperature, setTemperature] = useState<string>('');
  const [temperatureUnit, setTemperatureUnit] = useState<boolean>(false);
  const [enableTransferToCounter, setEnableTransferToCounter] = useState<boolean>(false);
  const [alwaysTransferToCounter, setAlwaysTransferToCounter] = useState<boolean>(false);
  const [expirePickups, setExpirePickups] = useState<boolean>(false);
  const [crewHome, setCrewHome] = useState<boolean>(false);
  const [pickupHome, setPickupHome] = useState<boolean>(false);
  const [queueHoldMinutes, setQueueHoldMinutes] = useState<string>('60');
  const [cabinetHoldMinutes, setCabinetHoldMinutes] = useState<string>('30');

  const upsertEditedCabinet = async() => {
    if (!cabinetObject) {
      return;
    }
    cabinetObject.pucs = hardwareConfig;
    
    if (intermediateStoreId && !hasStoreIdError) {
      cabinetObject.store_id = intermediateStoreId;
    }
    
    const payload = Cabinet.payload(cabinetObject);
    if (orderManagerCode) {
      // Remove any existing 'display_order_manager' codes
      if (payload.properties.app.passwords.codes) {
        const codeObjects = Object.entries(payload.properties.app.passwords.codes).map( (k) => {
          return(k);
        });

        payload.properties.app.passwords.codes = {};
        codeObjects.forEach( (oneCode) => {
          // add new `display_order_manager` code
          if (oneCode[1] !== "display_order_manager") {
            payload.properties.app.passwords.codes[oneCode[0]] = oneCode[1];
          }
        });
      }

      payload.properties.app.passwords.codes[orderManagerCode] = 'display_order_manager';
    }

    if (orderManagerQrCode) {
      // Remove any existing 'display_order_manager' codes
      if (payload.properties.app.passwords.barcodes) {
        const codeObjects = Object.entries(payload.properties.app.passwords.barcodes).map( (k) => {
          return(k);
        });

        payload.properties.app.passwords.barcodes = {};
        codeObjects.forEach( (oneCode) => {
          // add new `display_order_manager` code
          if (oneCode[1] !== "display_order_manager") {
            payload.properties.app.passwords.codes[oneCode[0]] = oneCode[1];
          }
        });
      }

      payload.properties.app.passwords.barcodes[orderManagerQrCode] = 'display_order_manager';
    }

    if (queueHoldMinutes) {
      // Set timeout_queue from queueHoldMinutes value
      // Displayed value is in minutes; stored value is in seconds
      payload.properties.app.order.timeout_queue = (parseInt(queueHoldMinutes) * 60).toString();
    }

    if (cabinetHoldMinutes) {
      // Set timeout_cabinet from cabinetHoldMinutes value
      // Displayed value is in minutes; stored value is in seconds
      payload.properties.app.order.timeout_cabinet = (parseInt(cabinetHoldMinutes) * 60).toString();
    }

    delete payload.properties.app.ui_assets_s3_url;
    delete payload.properties.app.store_properties;
    delete payload.properties.app.locale;
    delete payload.properties.app.mode;
    delete payload.properties.app.displays;
    delete payload.properties.app.organization_order_sources;
    delete payload.properties.app.order.code_start;
    delete payload.properties.app.order.code_end;

    // If cabinet has lighting, append the default lighting parameters
    if (hasLighting) {
      const lightingParams = {
        "lighting": {
          "color_open": lightingColorOpen,
          "color_closed": lightingColorClosed,
          "fade_time_ms": 2000,
          "leds_per_locker": 30
        }
      };
      payload.properties = { ...payload.properties, ...lightingParams};
    } else {
      // if no lighting, remove the lighting object
      delete payload.properties.lighting;
    }

    // If temperature value is present, append all the temperature properties
    if (temperature) {
      // Add the hardware temp object
      const heatingParams = {"hardware": {"global": {"lockers_heat_set": parseInt(temperature), "lockers_heat_enable": "on"}}};
      // Temperature Unit toggled "on" for celsius, or "off" for fahrenheit. Append properties.app with the units object
      const tempUnit = (!temperatureUnit) ? 'fahrenheit' : 'celsius';
      const tempUnits = {"units": {"temperature": tempUnit}};

      payload.properties.app = { ...payload.properties.app, ...tempUnits};
      payload.properties = { ...payload.properties, ...heatingParams};
    } else {
      delete payload.properties.hardware;
      delete payload.properties.app.units;
    }

    payload.properties.app.crew_home = (!crewHome ? 'manage' : 'create_new');
    payload.properties.app.pickup_home = (!pickupHome ? 'start' : 'select_pickup');
    payload.properties.app.expire_pickups = expirePickups;
    payload.properties.app.enable_transfer_to_counter = enableTransferToCounter;
    payload.properties.app.always_transfer_to_counter = alwaysTransferToCounter;

    const response = await upsertCabinet(store.getState().authorization.cloudKey, payload);
    if (response?.status !== 201) {
      let detailString = '';
      Object.keys(response.response.data.details).forEach(k => {
        detailString += response.response.data.details[k] + '\n';
      });
      alert(`\n${response.response.data.error}:\n${detailString}`);
    } else {
      if (isNewCabinet) {
        const deviceResponse = await putCabinetDevice(store.getState().authorization.cloudKey, cabinetId, description, deviceSerial);
        if (deviceResponse?.status !== 201) {
          let detailString = '';
          Object.keys(deviceResponse.response.data.details).forEach(k => {
            detailString += deviceResponse.response.data.details[k];
          });
          alert(`\n${deviceResponse.response.data.error}:\n${detailString}`);
        }
      }
      refresh();
      setDialog(false);
    }
  };

  const setCabinetData = (cabinet: Cabinet) => {
    setCabinetObject(cabinet);
    setStoreId(cabinet.store_id);
    setIntermediateStoreId(cabinet.store_id);
    setCabinetId(cabinet.id);
    setDescription(cabinet.description);
    setBusinessName(cabinet.business_name || '');
    setBusinessAddress(cabinet.business_address || '');
    setBusinessPhone(cabinet.business_phone || '');
    setCabinetBrand(cabinet.properties.app.brand || 'carterhoffmann');
    setCabinetLocation(cabinet.cabinet_location || '');
    setHardwareConfig(cabinet.pucs);
    setSmsCountries(cabinet.sms_countries || ['US']);
    setEnableSms(cabinet.enable_order_sms!);
    setEnableOrderPrint(cabinet.enable_order_print!);
    setOrderManagerCode(Object.keys(cabinet.properties?.app?.passwords?.codes)[0]);
    setOrderManagerQrCode(Object.keys(cabinet.properties?.app?.passwords?.barcodes)[0]);
    // Value is stored in seconds. To display minutes. divide by 60.
    setQueueHoldMinutes((parseInt(cabinet.properties.app.order.timeout_queue) / 60).toString());
    setCabinetHoldMinutes((parseInt(cabinet.properties.app.order.timeout_cabinet) / 60).toString());
    // if lighting object is present hasLIghting is true; otherwise false
    const cabHasLighting = (cabinet.properties.lighting) ? true : false;
    setHasLighting(cabHasLighting);

    // If there is lighting, set the open and closed colors from cabinet properties
    let cabColorOpen = lightingColorOpen;

    if (cabHasLighting && cabinet.properties?.lighting?.color_open) {
      cabColorOpen = cabinet.properties.lighting.color_open;
    }
    setLightingColorOpen(cabColorOpen);

    let cabColorClosed = lightingColorClosed;

    if (cabHasLighting && cabinet.properties?.lighting?.color_closed) {
      cabColorClosed = cabinet.properties.lighting.color_closed;
    }
    setLightingColorClosed(cabColorClosed);

    // set the temp unit to a boolean for the toggle
    const tempUnit = (cabinet.properties.app.units?.temperature === 'celsius') ? true : false;
    setTemperatureUnit(tempUnit);
    // Only need to set a temperature value if one exists
    if (cabinet.properties.hardware) {
      setTemperature(cabinet.properties.hardware?.global?.lockers_heat_set);
    }

    // Only want the code for display_order_manager, not for display_configuration
    const orderManagerCode = Object.keys(cabinet.properties?.app?.passwords?.codes).find( (key) => cabinet.properties?.app?.passwords?.codes[key] === 'display_order_manager');

    if (orderManagerCode) {
      setOrderManagerCode(orderManagerCode);
    }

    // OrderManagerQrCode for display_order_manager
    const orderManagerQrCode = Object
      .keys(cabinet.properties?.app?.passwords?.barcodes)
      .find(
        (key) => cabinet.properties?.app?.passwords?.barcodes[key] === 'display_order_manager'
      );

    if (orderManagerQrCode) {
      setOrderManagerQrCode(orderManagerQrCode);
    }

    setCrewHome(cabinet.properties?.app?.crew_home === 'manage' ? false : true);
    setPickupHome(cabinet.properties?.app?.pickup_home === 'start' ? false : true);
    setExpirePickups(cabinet.properties?.app?.expire_pickups || false);
    setEnableTransferToCounter(cabinet.properties?.app?.enable_transfer_to_counter || false);
    setAlwaysTransferToCounter(cabinet.properties?.app?.always_transfer_to_counter || false);
  };
  
  useEffect(() => {
    const fetchCabinet = async () => {
      const fetchedCabinetConfig = await getCabinetConfiguration(store.getState().authorization.cloudKey, editingCabinet);
      const concreteCabinet = new Cabinet(fetchedCabinetConfig.data);
      setCabinetData(concreteCabinet);
    };

    if (editingCabinet) {
      setIsNewCabinet(false);
      fetchCabinet();
    } else {
      setIsNewCabinet(true);
      setIsFormValid(false);
      const newCabinet = Cabinet.generateNewCabinet('', selectedStoreId);
      setCabinetData(newCabinet);
    }
  }, []);

  const applyHardwareChanges = (changedHardware: Array<Puc>) => {
    cabinetObject!.pucs = changedHardware;
    setHardwareConfig(changedHardware);
  };

  const changeSmsCountries = (event: SelectChangeEvent<typeof smsCountries>) => {
    const {
      target: { value },
    } = event;
    const currentList = typeof value === 'string' ? value.split(',') : value;
    cabinetObject!.sms_countries = currentList;
    setSmsCountries(currentList);
  };
  
  const renderDeviceSerial = () => {
    if (isNewCabinet) {
      return <TextField
      inputProps={{ maxLength: 16 }}
        sx={{
          width: '350px',
          marginTop: '10px',
          marginBottom: '25px',
        }}
        label='ELO Device Serial'
        value={deviceSerial}
        onChange={(e) => {
          if (!e.target.value) {
            alert('New cabinets require an ELO serial');
            setIsFormValid(false);
          } else {
            setIsFormValid(true);
          }
          setDeviceSerial(e.target.value);  
        }} />;
    } else {
      return <div></div>;
    }
  };

  const handleMovingCabinet = () => {
    setIntermediateStoreId(storeId);
    setHasStoreIdError(false);
    setStoreIdErrorText('');
    setMovingCabinet(!movingCabinet);
  };

  const validate = () => {
    const invalidDetails = !deviceSerial || !description || !businessName || !businessAddress || !businessPhone || !cabinetLocation || !orderManagerCode || !cabinetBrand || movingCabinet;
    if (invalidDetails) {
      setIsFormValid(false);
    } else {
      setIsFormValid(true);
    }
  };
  
  useEffect(() => {
    validate();
  }, [deviceSerial, description, businessAddress, businessName, businessPhone, cabinetLocation, cabinetBrand, orderManagerCode, movingCabinet]);

  return (
    <Box height='100%'>
      <DialogTitle
        display='flex'
        flexDirection='row'
        alignItems='center'
        sx={{
          height: '100px',
          width: '100%'
        }}>
        <div style={{
            fontWeight: '500',
            color: '#121F35',
            fontSize: '40px',
            marginRight: '100px'
          }}>
          {'Cabinet Editor'}
        </div>
        <div>
          <Button
            variant='contained'
            sx={{
              width: '100px',
              fontSize: '24px',
              marginRight: '25px'
            }}
            disabled={!isFormValid}
            onClick={async() => { await upsertEditedCabinet(); }}>
            Save
          </Button>
          <Button
            variant='contained'
            sx={{
              width: '100px',
              fontSize: '24px'
            }}
            onClick={() => {
              setDialog(false);
            }}>
            Cancel
          </Button>
        </div>
      </DialogTitle>
      <DialogContent
        sx={{
          height: '650px'
        }}>
        <Box
          sx={{
            width: '100%',
            display: 'flex',
            flexDirection: 'column'
          }}>
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs value={value} onChange={handleChange} aria-label="basic tabs example">
              <Tab label="Cabinet Settings" {...a11yProps(0)} />
              <Tab label="Cabinet Details" {...a11yProps(1)} />
              <Tab label="Cabinet Hardware" {...a11yProps(2)} />
            </Tabs>
          </Box>
          <CabinetTabPanel
            value={value}
            index={0}>
            <Box 
              sx={{
                height: '300px',
                width: '1000px',
                display: 'flex',
                flexDirection: 'column',
              }}>
              <div style={{
                display: 'flex',
                flexDirection: 'row',
                width: '100%'
              }}>
                <TextField
                  inputProps={{ maxLength: 24 }}
                  sx={{
                    width: '350px',
                    marginTop: '10px',
                    marginBottom: '25px',
                    marginRight: '24px',
                  }}
                  disabled
                  label='ID'
                  value={cabinetId} />
                <div style={{
                  display: 'flex',
                  flexDirection: 'row',
                  }}>
                  <TextField
                    error={hasStoreIdError}
                    helperText={storeIdErrorText}
                    sx={{
                      width: '350px',
                      marginTop: '10px',
                      marginBottom: '25px',
                      marginLeft: '24px',
                    }}
                    disabled={!movingCabinet}
                    label='Store ID'
                    value={intermediateStoreId}
                    onBlur = {(e) => {
                      if (!e.target.value) {
                        setStoreIdErrorText('Invalid store ID to move cabinet');
                        setHasStoreIdError(true);
                      } else {
                        setStoreIdErrorText('');
                        setHasStoreIdError(false);
                      }
                    }}
                    onChange = {(e) => {
                      if (!stores.find(x => x === e.target.value)) {
                        setStoreIdErrorText('Invalid store ID to move cabinet');
                        setHasStoreIdError(true);
                        setIsFormValid(false);
                      } else {
                        setStoreIdErrorText('');
                        setHasStoreIdError(false);
                        setIsFormValid(true);
                      }
                      setIntermediateStoreId(e.target.value);
                    }} />
                  <Button
                    variant='contained'
                    sx={{
                      width: '200px',
                      height: '60px',
                      fontSize: '20px',
                      marginLeft: '20px',
                      marginTop: '8px',
                    }}
                    onClick={handleMovingCabinet}>
                    {movingCabinet
                      ? 'Cancel'
                      : 'Move Cabinet'}
                  </Button>
                </div>
              </div>
              {renderDeviceSerial()}

              <div style={{
                width: '100%',
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'space-between',
                marginBottom: '12px',
              }}>
                <div style={{
                  display: 'flex'
                }}>
                  <Typography>Lighting</Typography>
                  <Switch
                    checked={hasLighting}
                    onChange={(e) => {
                      setHasLighting(e.target.checked);
                  }} />

                  <TextField 
                    sx={{
                      width: '190px',
                      marginRight: '8px'
                    }}
                    error={hasLightingColorOpenError}
                    helperText={lightingColorOpenErrorText}
                    disabled={!hasLighting}
                    required={hasLighting}
                    label="Lighting Color Open"
                    value={lightingColorOpen}
                    onBlur = {(e) => {
                      if (!e.target.value) {
                        setLightingColorOpenErrorText('Lighting Color Open is required');
                        setHasLightingColorOpenError(true);
                      } else {
                        setLightingColorOpenErrorText('');
                        setHasLightingColorOpenError(false);
                      }
                    }}
                    onChange={(e) => {
                      setLightingColorOpen(e.target.value);
                    }} />

                  <TextField 
                    sx={{
                      width: '190px',
                      marginLeft: '8px'
                    }}
                    error={hasLightingColorClosedError}
                    helperText={lightingColorClosedErrorText}
                    disabled={!hasLighting}
                    required={hasLighting}
                    label="Lighting Color Closed"
                    value={lightingColorClosed}
                    onBlur = {(e) => {
                      if (!e.target.value) {
                        setLightingColorClosedErrorText('Lighting Color Closed is required');
                        setHasLightingColorClosedError(true);
                      } else {
                        setLightingColorClosedErrorText('');
                        setHasLightingColorClosedError(false);
                      }
                    }}
                    onChange={(e) => {
                      setLightingColorClosed(e.target.value);
                    }} />
                </div>
                <div style={{
                  display: 'flex'
                }}>
                  <TextField 
                    sx={{
                      width: '120px',
                      marginRight: '15px'
                    }}
                    label="Temperature"
                    value={temperature}
                    onChange={(e) => {
                      setTemperature(e.target.value);
                    }} />
                  <div style={{
                    display: 'flex',
                    marginTop: '10px'
                  }}>
                    <Typography style={{
                      marginTop: '8px'
                    }}>F</Typography>
                    <Switch 
                      checked={temperatureUnit}
                      onChange={(e) => {
                        setTemperatureUnit(e.target.checked);
                      }} />
                    <Typography style={{
                      marginTop: '8px'
                    }}>C</Typography>
                  </div>
                </div>
              </div>

              <div style={{
                width: '35%',
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'space-between',
                marginBottom: '16px'
              }}>
                <div>
                  <Typography>Enable Order Print</Typography>
                  <Switch
                    checked={enableOrderPrint}
                    onChange={(e) => {
                      cabinetObject!.enable_order_print = e.target.checked;
                      setEnableOrderPrint(e.target.checked);
                    }} />
                </div>
                
                <div>
                  <Typography>ExpirePickups</Typography>
                  <Switch
                    checked={expirePickups}
                    onChange={(e) => {
                      setExpirePickups(e.target.checked);
                  }} />
                </div>
              </div>
              <div style={{
                display: 'flex',
                flexDirection: 'row',
                marginBottom: '16px'
              }}>
                <div style={{
                  width: '33%'
                }}>
                  <Typography>Enable SMS</Typography>
                  <Switch
                    checked={enableSms}
                    onChange={(e) => {
                      cabinetObject!.enable_order_sms = e.target.checked;
                      setEnableSms(e.target.checked);
                    }} />
                </div>

                <div style={{
                  width: '100%'
                }}>
                  <Typography>SMS COUNTRIES</Typography>
                  <Select sx={{
                    width: '40%'
                  }}
                    multiple
                    value={smsCountries}
                    onChange={changeSmsCountries}
                    input={<OutlinedInput label="Country" />}
                    MenuProps={MenuProps}>
                    {allCountries.map((country) => (
                      <MenuItem
                        key={country.iso2}
                        value={country.iso2}
                        style={getStyles(country.iso2, smsCountries, theme)}>
                        {country.name}
                      </MenuItem>
                    ))}
                  </Select>
                </div>
              </div>

              <div style={{
                width: '45%',
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'space-between',
                marginBottom: '16px'
              }}>
                <div>
                  <Typography>Enable Transfer to Counter</Typography>
                  <Switch
                    checked={enableTransferToCounter}
                    onChange={(e) => {
                      cabinetObject!.enable_order_print = e.target.checked;
                      setEnableTransferToCounter(e.target.checked);
                    }} />
                </div>
                
                <div>
                  <Typography>Always Transfer to Counter</Typography>
                  <Switch
                    checked={alwaysTransferToCounter}
                    onChange={(e) => {
                      setAlwaysTransferToCounter(e.target.checked);
                  }} />
                </div>
              </div>

              <div style={{
                width: '45%',
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'space-between',
                marginBottom: '16px'
              }}>
                <div>
                  <Typography>Crew Home Screen</Typography>
                  <div style={{
                    display: 'flex',
                  }}>
                    <Typography style={{
                      marginTop: '8px'
                    }}>Manage</Typography>
                    <Switch 
                      checked={crewHome}
                      onChange={(e) => {
                        setCrewHome(e.target.checked);
                      }} />
                    <Typography style={{
                      marginTop: '8px'
                    }}>Create New</Typography>
                  </div>
                </div>
                
                <div>
                  <Typography>Pickup Home Screen</Typography>
                  <div style={{
                    display: 'flex',
                  }}>
                    <Typography style={{
                      marginTop: '8px'
                    }}>Home</Typography>
                    <Switch 
                      checked={pickupHome}
                      onChange={(e) => {
                        setPickupHome(e.target.checked);
                      }} />
                    <Typography style={{
                      marginTop: '8px'
                    }}>Select Pickup</Typography>
                  </div>
                </div>
              </div>
            </Box>
          </CabinetTabPanel>
          <CabinetTabPanel value={value} index={1}>
            <Box
              sx={{
                height: '300px',
                width: '50%',
                display: 'inline-flex',
                flexDirection: 'column',
              }}>
              <TextField
                error={hasDescriptionError}
                helperText={descriptionErrorText}
                inputProps={{ maxLength: 100 }}
                sx={{
                  width: '450px',
                  marginBottom: '25px'
                }}
                label='Description'
                required
                value={description}
                onBlur = {(e) => {
                  if (!e.target.value) {
                    setDescriptionErrorText('Description is required');
                    setHasDescriptionError(true);
                  } else {
                    setDescriptionErrorText('');
                    setHasDescriptionError(false);
                  }
                }}
                onChange = {(e) => {
                  cabinetObject!.description = e.target.value;
                  setDescription(e.target.value);
                }} />
              <TextField
                error={hasBusinessNameError}
                helperText={businessNameErrorText}
                inputProps={{ maxLength: 50 }}
                sx={{
                  width: '450px',
                  marginBottom: '25px'
                }}
                label='Business Name'
                value={businessName}
                required
                onBlur = {(e) => {
                  if (!e.target.value) {
                    setBusinessNameErrorText('Business Name is required');
                    setHasBusinessNameError(true);
                  } else {
                    setBusinessNameErrorText('');
                    setHasBusinessNameError(false);
                  }
                }}
                onChange = {(e) => {
                  cabinetObject!.business_name = e.target.value;
                  setBusinessName(e.target.value);
                }} />
              <TextField
                error={hasBusinessAddressError}
                helperText={businessAddressErrorText}
                inputProps={{ maxLength: 100 }}
                sx={{
                  width: '450px',
                  marginBottom: '25px'
                }}
                required
                label='Business Address'
                value={businessAddress}
                onBlur = {(e) => {
                  if (!e.target.value) {
                    setBusinessAddressErrorText('Business Address is required');
                    setHasBusinessAddressError(true);
                  } else {
                    setBusinessAddressErrorText('');
                    setHasBusinessAddressError(false);
                  }
                }}
                onChange = {(e) => {
                  cabinetObject!.business_address = e.target.value;
                  setBusinessAddress(e.target.value);
                }} />
              <TextField
                error={hasBusinessPhoneError}
                helperText={businessPhoneErrorText}
                inputProps={{ maxLength: 15 }}
                sx={{
                  width: '450px',
                  marginBottom: '25px'
                }}
                required
                label='Business Phone'
                value={businessPhone}
                onBlur = {(e) => {
                  if (!e.target.value) {
                    setBusinessPhoneErrorText('Business Phone is required');
                    setHasBusinessPhoneError(true);
                  } else {
                    setBusinessPhoneErrorText('');
                    setHasBusinessPhoneError(false);
                  }
                }}
                onChange = {(e) => {
                  cabinetObject!.business_phone = e.target.value;
                  setBusinessPhone(e.target.value);
                }} />
              <TextField
                error={hasCabinetLocationError}
                helperText={cabinetLocationErrorText}
                inputProps={{ maxLength: 75 }}
                sx={{
                  width: '450px',
                  marginBottom: '25px'
                }}
                required
                label='Location'
                value={cabinetLocation}
                onBlur = {(e) => {
                  if (!e.target.value) {
                    setCabinetLocationErrorText('Location is required');
                    setHasCabinetLocationError(true);
                  } else {
                    setCabinetLocationErrorText('');
                    setHasCabinetLocationError(false);
                  }
                }}
                onChange = {(e) => {
                  cabinetObject!.cabinet_location = e.target.value;
                  setCabinetLocation(e.target.value);
                }} />
            </Box>
            <Box
            
              sx={{
                height: '300px',
                width: '50%',
                display: 'inline-flex',
                flexDirection: 'column',
              }}>
              <TextField
                error={hasBrandError}
                helperText={brandErrorText}
                inputProps={{ maxLength: 36 }}
                sx={{
                  width: '450px',
                  marginBottom: '25px'
                }}
                required
                label='Brand'
                value={cabinetBrand}
                onBlur = {(e) => {
                  if (!e.target.value) {
                    setBrandErrorText('Brand is required');
                    setHasBrandError(true);
                  } else {
                    setBrandErrorText('');
                    setHasBrandError(false);
                  }
                }}
                onChange = {(e) => {
                  cabinetObject!.properties.app.brand = e.target.value;
                  setCabinetBrand(e.target.value);
                }} />
              <TextField
                error={hasOrderManagerCodeError}
                helperText={orderManagerCodeErrorText}
                inputProps={{ maxLength: 36 }}
                sx={{
                  width: '450px',
                  marginBottom: '25px'
                }}
                required
                label='Order Manager Code'
                value={orderManagerCode}
                onBlur = {(e) => {
                  if (!e.target.value) {
                    setOrderManagerCodeErrorText('Order ManagerCode is required');
                    setHasOrderManagerCodeError(true);
                  } else {
                    setOrderManagerCodeErrorText('');
                    setHasOrderManagerCodeError(false);
                  }
                }}
                onChange = {(e) => {
                  setOrderManagerCode(e.target.value);
                }} />
              <TextField
                inputProps={{ maxLength: 36 }}
                sx={{
                  width: '450px',
                  marginBottom: '25px'
                }}
                label='Order Manager QR Code'
                value={orderManagerQrCode}
                onChange = {(e) => {
                  setOrderManagerQrCode(e.target.value);
                }} />
              <TextField
                inputProps={{ maxLength: 10 }}
                sx={{
                  width: '450px',
                  marginBottom: '25px'
                }}
                label="Queue Hold Time (minutes)"
                value={queueHoldMinutes}
                onChange = {(e) => {
                  setQueueHoldMinutes(e.target.value);
                }} />
              <TextField
                inputProps={{ maxLength: 10 }}
                sx={{
                  width: '450px',
                  marginBottom: '25px'
                }}
                label="Cabinet Hold Time (minutes)"
                value={cabinetHoldMinutes}
                onChange = {(e) => {
                  setCabinetHoldMinutes(e.target.value);
                }} />
            </Box>
          </CabinetTabPanel>
          <CabinetTabPanel value={value} index={2}>
            <PucEditor
              pucs={hardwareConfig}
              applyHardwareChanges={applyHardwareChanges}
              canvasWidth={776}
              canvasHeight={540} />
          </CabinetTabPanel>
        </Box>
      </DialogContent>
    </Box>
  );
}

export default CabinetDialog;
